import {
	BILL_PERIODS,
	type BillPeriod,
	type BillPeriodValues,
	type ComparatorBill,
	type ComparatorOffer,
	type ComparatorSegment,
	type ComparatorTariff,
	type ComparatorTaxConfig,
} from '../dtos/comparator/comparator.dto';

/**
 * Electricity bill comparator (Fase 3 - Comparador, Linear GK-227). The rules
 * live in the spec v2 of 2026-10-15, section 2; `docs/COMPARADOR.md` maps each
 * one to the code. Pure functions only: the reader, the screens and the public
 * endpoints all feed this same engine.
 */

/** Values confirmed by ERIS (taxes 2026-10-13, adjustment services 2026-10-28). Editable in the configuration. */
export const DEFAULT_COMPARATOR_TAX_CONFIG: ComparatorTaxConfig = {
	electricityTaxRatePct: 5.11269632,
	electricityTaxMinPerMwh: 1,
	vatRatePct: 21,
	adjustmentServicesPricePerKwh: 0.025,
};

/** Up to this contracted power a 2.0TD supply is residential; above it, SME. */
export const RESIDENTIAL_MAX_POWER_KW = 10;

/** Accepted gap between a recalculated amount and the printed one. */
export const BILL_CHECK_TOLERANCE = 0.02;

/** Power periods the optimizer lowers; in 3.0TD/6.1TD, P6 keeps the current contracted power. */
const OPTIMIZABLE_PERIODS: readonly BillPeriod[] = ['P1', 'P2', 'P3', 'P4', 'P5'];

/** Optimized powers are rounded up to whole kW (ERIS, 2026-10-28). */
export const OPTIMIZER_STEP_KW = 1;

/**
 * 3.0TD/6.1TD calendar (ERIS table, 2026-10-13): the two periods each month
 * uses from 8 to 24 h on working days. P6 applies every month (0–8 h,
 * weekends and holidays).
 */
const MONTH_PERIODS: Record<number, readonly [BillPeriod, BillPeriod]> = {
	1: ['P1', 'P2'],
	2: ['P1', 'P2'],
	3: ['P2', 'P3'],
	4: ['P4', 'P5'],
	5: ['P4', 'P5'],
	6: ['P3', 'P4'],
	7: ['P1', 'P2'],
	8: ['P3', 'P4'],
	9: ['P3', 'P4'],
	10: ['P4', 'P5'],
	11: ['P2', 'P3'],
	12: ['P1', 'P2'],
};

/** Rounds half away from zero to cents, immune to binary noise (1.005 → 1.01). */
export const roundCents = (value: number): number => {
	const cents = Math.round(Number((Math.abs(value) * 100).toPrecision(15)));
	return (Math.sign(value) * cents) / 100 || 0;
};

const sumAmounts = (items: readonly { amount: number }[]): number => items.reduce((acc, item) => acc + item.amount, 0);

const sumPeriods = (values: BillPeriodValues): number =>
	BILL_PERIODS.reduce((acc, period) => acc + (values[period] ?? 0), 0);

/** The part of the printed surplus deducted before taxes, from the energy term. */
const compensatedBeforeTaxes = (bill: ComparatorBill): number =>
	bill.surplus?.compensation === 'ENERGY_TERM' ? bill.surplus.compensatedAmount : 0;

/** The part deducted from the bill total, after taxes (Gana Energía). */
const compensatedOnTotal = (bill: ComparatorBill): number =>
	bill.surplus?.compensation === 'BILL_TOTAL' ? bill.surplus.compensatedAmount : 0;

const maxPeriod = (values: BillPeriodValues): number => Math.max(0, ...BILL_PERIODS.map((p) => values[p] ?? 0));

const TARIFF_SPELLINGS: Record<string, ComparatorTariff> = {
	'20TD': 'T_20TD',
	'30TD': 'T_30TD',
	'61TD': 'T_61TD',
};

/** Reads a tariff however the bill writes it («2.0TD», «20TD», «3.0 TD»). Null when unsupported. */
export const normalizeAccessTariff = (raw: string | null | undefined): ComparatorTariff | null => {
	if (!raw) return null;
	const compact = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
	return TARIFF_SPELLINGS[compact.replace(/^T/, '')] ?? null;
};

/** Residential up to 10 kW, SME above; every 3.0TD/6.1TD is SME. The highest period decides. */
export const billSegment = (tariff: ComparatorTariff, contractedPowerKw: BillPeriodValues): ComparatorSegment =>
	tariff === 'T_20TD' && maxPeriod(contractedPowerKw) <= RESIDENTIAL_MAX_POWER_KW ? 'RESIDENTIAL' : 'SME';

/** Electricity tax: the percentage, never below the floor per MWh consumed. */
export const electricityTax = (base: number, consumptionKwh: number, config: ComparatorTaxConfig): number =>
	roundCents(
		Math.max((base * config.electricityTaxRatePct) / 100, (consumptionKwh / 1000) * config.electricityTaxMinPerMwh),
	);

// ============================================
// READING CHECK (acceptance criterion: printed bases and total ±0.02 €)
// ============================================

export interface BillCheck {
	electricityTaxBase: number;
	vatBase: number;
	total: number;
	/** Recalculated minus printed; null when the bill does not print it. */
	electricityTaxBaseDifference: number | null;
	vatBaseDifference: number | null;
	totalDifference: number;
	ok: boolean;
}

/**
 * Electricity tax base (spec 2.4 i): power + energy + regulated charges billed
 * apart − surplus compensated on the energy term − discounts + reactive +
 * power excess + social bonus. Meter rental and services stay out, and so
 * does a surplus deducted from the bill total.
 */
export const billElectricityTaxBase = (bill: ComparatorBill): number =>
	roundCents(
		bill.powerAmount +
			bill.energyAmount +
			sumAmounts(bill.regulatedCharges) -
			compensatedBeforeTaxes(bill) -
			sumAmounts(bill.discounts) +
			bill.reactiveAmount +
			bill.powerExcessAmount +
			bill.socialBonusAmount,
	);

/**
 * Rebuilds the bill with the taxes it prints, to tell a misread from a real
 * difference. This is what the review screen shows next to the printed total;
 * the comparison itself uses the configured rates (`priceCurrentBill`).
 */
export const checkBill = (bill: ComparatorBill): BillCheck => {
	const { printed } = bill;
	const electricityTaxBase = billElectricityTaxBase(bill);
	const services = sumAmounts(bill.services);
	// Services carry their own VAT, either in a separate line or inside the main one,
	// so the printed VAT bases are compared as a whole.
	const vatBase = roundCents(electricityTaxBase + printed.electricityTaxAmount + bill.meterRentalAmount + services);
	const printedVatBases = printed.vat.every((line) => line.base !== null)
		? roundCents(printed.vat.reduce((acc, line) => acc + (line.base ?? 0), 0))
		: null;
	const total = roundCents(vatBase + sumAmounts(printed.vat) + printed.otherTaxesAmount - compensatedOnTotal(bill));

	const electricityTaxBaseDifference =
		printed.electricityTaxBase === null ? null : roundCents(electricityTaxBase - printed.electricityTaxBase);
	const vatBaseDifference = printedVatBases === null ? null : roundCents(vatBase - printedVatBases);
	const totalDifference = roundCents(total - printed.total);
	const within = (difference: number | null) => difference === null || Math.abs(difference) <= BILL_CHECK_TOLERANCE;

	return {
		electricityTaxBase,
		vatBase,
		total,
		electricityTaxBaseDifference,
		vatBaseDifference,
		totalDifference,
		ok: within(electricityTaxBaseDifference) && within(vatBaseDifference) && within(totalDifference),
	};
};

// ============================================
// COST OF ONE MONTH (current bill or an offer)
// ============================================

export interface BillCost {
	power: number;
	energy: number;
	regulatedCharges: number;
	/** Surplus deducted from the energy term, before taxes. */
	surplusCompensation: number;
	/** Surplus deducted from the total, after taxes (BILL_TOTAL offers and bills). */
	surplusCompensationOnTotal: number;
	discounts: number;
	reactive: number;
	powerExcess: number;
	socialBonus: number;
	electricityTaxBase: number;
	electricityTax: number;
	meterRental: number;
	vatBase: number;
	vat: number;
	total: number;
	/** Surplus value above the cap, kept by the client as virtual-battery balance. */
	virtualBattery: number;
	/** Total minus the virtual battery: what both sides are compared on (spec 2.4 d). */
	comparableTotal: number;
}

type CostTerms = Pick<
	BillCost,
	| 'power'
	| 'energy'
	| 'regulatedCharges'
	| 'surplusCompensation'
	| 'surplusCompensationOnTotal'
	| 'discounts'
	| 'virtualBattery'
>;

const buildCost = (bill: ComparatorBill, terms: CostTerms, config: ComparatorTaxConfig): BillCost => {
	const electricityTaxBase = roundCents(
		terms.power +
			terms.energy +
			terms.regulatedCharges -
			terms.surplusCompensation -
			terms.discounts +
			bill.reactiveAmount +
			bill.powerExcessAmount +
			bill.socialBonusAmount,
	);
	const tax = electricityTax(electricityTaxBase, bill.totalConsumptionKwh, config);
	const vatBase = roundCents(electricityTaxBase + tax + bill.meterRentalAmount);
	const vat = roundCents((vatBase * config.vatRatePct) / 100);
	const total = roundCents(vatBase + vat - terms.surplusCompensationOnTotal);
	return {
		...terms,
		reactive: bill.reactiveAmount,
		powerExcess: bill.powerExcessAmount,
		socialBonus: bill.socialBonusAmount,
		electricityTaxBase,
		electricityTax: tax,
		meterRental: bill.meterRentalAmount,
		vatBase,
		vat,
		total,
		comparableTotal: roundCents(total - terms.virtualBattery),
	};
};

/** The bill's own month with the configured taxes, services left out (they are shown apart). */
export const priceCurrentBill = (bill: ComparatorBill, config: ComparatorTaxConfig): BillCost =>
	buildCost(
		bill,
		{
			power: bill.powerAmount,
			energy: bill.energyAmount,
			regulatedCharges: roundCents(sumAmounts(bill.regulatedCharges)),
			surplusCompensation: compensatedBeforeTaxes(bill),
			surplusCompensationOnTotal: compensatedOnTotal(bill),
			discounts: roundCents(sumAmounts(bill.discounts)),
			virtualBattery: bill.surplus?.virtualBatteryAmount ?? 0,
		},
		config,
	);

export type OfferPricingStatus = 'PRICED' | 'MISSING_PERIOD_CONSUMPTION' | 'MISSING_OFFER_PRICE';

export type OfferPricing =
	| { status: 'PRICED'; cost: BillCost }
	| { status: Exclude<OfferPricingStatus, 'PRICED'>; periods: BillPeriod[] };

/**
 * The same month priced with an offer (spec 2.4): power and energy at the
 * offer's prices, adjustment services when the offer bills them apart, the
 * surplus capped at the energy cost (ENERGY_TERM) or at the bill total
 * (BILL_TOTAL) with the rest going to the virtual battery, and every
 * distributor or regulated amount copied from the bill.
 * Offers carry no discounts: those are already in their price.
 */
export const priceOffer = (
	bill: ComparatorBill,
	offer: ComparatorOffer,
	config: ComparatorTaxConfig,
	powerKw: BillPeriodValues = bill.contractedPowerKw,
): OfferPricing => {
	const missingPrices: BillPeriod[] = [];
	let power = 0;
	for (const period of BILL_PERIODS) {
		const kw = powerKw[period] ?? 0;
		if (kw <= 0) continue;
		const price = offer.powerPricePerKwDay[period];
		if (price === undefined) missingPrices.push(period);
		else power += roundCents(kw * price * bill.days);
	}

	let energy = 0;
	if (offer.energyPriceType === 'FLAT') {
		if (offer.flatEnergyPricePerKwh === null) return { status: 'MISSING_OFFER_PRICE', periods: [] };
		energy = roundCents(bill.totalConsumptionKwh * offer.flatEnergyPricePerKwh);
	} else {
		if (sumPeriods(bill.consumptionKwh) <= 0 && bill.totalConsumptionKwh > 0) {
			return { status: 'MISSING_PERIOD_CONSUMPTION', periods: [] };
		}
		for (const period of BILL_PERIODS) {
			const kwh = bill.consumptionKwh[period] ?? 0;
			if (kwh <= 0) continue;
			const price = offer.energyPricePerKwh[period];
			if (price === undefined) missingPrices.push(period);
			else energy += roundCents(kwh * price);
		}
	}
	if (missingPrices.length > 0) {
		return { status: 'MISSING_OFFER_PRICE', periods: [...new Set(missingPrices)] };
	}

	const regulatedCharges =
		offer.adjustmentServices === 'SEPARATE'
			? roundCents(bill.totalConsumptionKwh * config.adjustmentServicesPricePerKwh)
			: 0;

	const terms: CostTerms = {
		power: roundCents(power),
		energy: roundCents(energy),
		regulatedCharges,
		surplusCompensation: 0,
		surplusCompensationOnTotal: 0,
		discounts: 0,
		virtualBattery: 0,
	};
	if (bill.surplus && bill.surplus.exportedKwh > 0 && offer.surplusPricePerKwh !== null) {
		const surplusValue = roundCents(bill.surplus.exportedKwh * offer.surplusPricePerKwh);
		if (offer.surplusCompensation === 'BILL_TOTAL') {
			// The cap is the total with taxes, so it is priced once without the surplus.
			const gross = buildCost(bill, terms, config).total;
			terms.surplusCompensationOnTotal = Math.max(0, Math.min(surplusValue, gross));
		} else {
			terms.surplusCompensation = Math.min(surplusValue, roundCents(energy + regulatedCharges));
		}
		terms.virtualBattery = roundCents(surplusValue - terms.surplusCompensation - terms.surplusCompensationOnTotal);
	}

	return { status: 'PRICED', cost: buildCost(bill, terms, config) };
};

// ============================================
// POWER OPTIMIZER (spec 2.5 b)
// ============================================

const ceilToStep = (value: number, step: number): number =>
	Number((Math.ceil(Number((value / step).toPrecision(12))) * step).toFixed(3));

/**
 * Powers lowered to the year's maximum demand, rounded up to whole kW. In
 * 3.0TD/6.1TD, P1–P5 drop and P6 keeps the current contracted power, so
 * P1 ≤ … ≤ P6 still holds; in 2.0TD both periods drop. A period already below
 * that maximum is never raised. Null when nothing drops or there is no P6 in
 * a 3.0TD/6.1TD. It is a proposal: ERIS decides whether to apply it
 * (2026-10-28), so the comparison shows it next to the plain result.
 */
export const optimizePower = (
	bill: ComparatorBill,
	annualMaxDemandKw: number,
	stepKw = OPTIMIZER_STEP_KW,
): BillPeriodValues | null => {
	const current = bill.contractedPowerKw;
	const isTwoPeriod = bill.tariff === 'T_20TD';
	if ((!isTwoPeriod && !current.P6) || annualMaxDemandKw <= 0) return null;
	const target = ceilToStep(annualMaxDemandKw, stepKw);
	const proposed: BillPeriodValues = isTwoPeriod ? {} : { P6: current.P6 };
	let lowered = false;
	for (const period of OPTIMIZABLE_PERIODS) {
		const kw = current[period];
		if (kw === undefined) continue;
		proposed[period] = Math.min(kw, target);
		lowered ||= proposed[period] < kw;
	}
	return lowered ? proposed : null;
};

// ============================================
// COMPARISON
// ============================================

export interface OfferSavings {
	cost: BillCost;
	savings: number;
	/** Savings over the recalculated month; null when that month costs nothing. */
	savingsPct: number | null;
}

export interface OfferComparison {
	offer: ComparatorOffer;
	status: OfferPricingStatus;
	/** Periods the offer has no price for, or empty. */
	missingPeriods: BillPeriod[];
	result: OfferSavings | null;
	/** Same offer with the optimized power, when an optimization was requested and applies. */
	optimized: OfferSavings | null;
}

export interface BillComparison {
	segment: ComparatorSegment;
	current: BillCost;
	/** Services removed from the bill and shown as extra savings (spec 2.4 j). */
	servicesAmount: number;
	optimizedPowerKw: BillPeriodValues | null;
	offers: OfferComparison[];
}

export interface CompareBillOptions {
	/** Last twelve months' maximum demand, to run the power optimizer. */
	annualMaxDemandKw?: number | null;
	optimizerStepKw?: number;
}

const savingsAgainst = (current: BillCost, cost: BillCost): OfferSavings => {
	const savings = roundCents(current.comparableTotal - cost.comparableTotal);
	const savingsPct =
		current.comparableTotal > 0 ? Math.round((savings / current.comparableTotal) * 10_000) / 100 : null;
	return { cost, savings, savingsPct };
};

/**
 * Compares one month of one supply against the whole active catalog of its
 * tariff and segment, flat and per-period offers alike (spec 2.4 c, 2.5 a),
 * from most to least savings. Offers that cannot be priced go last.
 */
export const compareBill = (
	bill: ComparatorBill,
	offers: readonly ComparatorOffer[],
	config: ComparatorTaxConfig,
	options: CompareBillOptions = {},
): BillComparison => {
	const segment = billSegment(bill.tariff, bill.contractedPowerKw);
	const current = priceCurrentBill(bill, config);
	const optimizedPowerKw = options.annualMaxDemandKw
		? optimizePower(bill, options.annualMaxDemandKw, options.optimizerStepKw)
		: null;

	const comparisons = offers
		.filter((offer) => offer.isActive && offer.tariff === bill.tariff && offer.segment === segment)
		.map((offer): OfferComparison => {
			const pricing = priceOffer(bill, offer, config);
			if (pricing.status !== 'PRICED') {
				return {
					offer,
					status: pricing.status,
					missingPeriods: pricing.periods,
					result: null,
					optimized: null,
				};
			}
			const optimizedPricing = optimizedPowerKw ? priceOffer(bill, offer, config, optimizedPowerKw) : null;
			return {
				offer,
				status: 'PRICED',
				missingPeriods: [],
				result: savingsAgainst(current, pricing.cost),
				optimized:
					optimizedPricing?.status === 'PRICED' ? savingsAgainst(current, optimizedPricing.cost) : null,
			};
		})
		.sort(
			(a, b) =>
				(b.result?.savings ?? Number.NEGATIVE_INFINITY) - (a.result?.savings ?? Number.NEGATIVE_INFINITY) ||
				a.offer.name.localeCompare(b.offer.name, 'es'),
		);

	return {
		segment,
		current,
		servicesAmount: roundCents(sumAmounts(bill.services)),
		optimizedPowerKw,
		offers: comparisons,
	};
};

// ============================================
// 3.0TD/6.1TD CALENDAR CHECK (spec 2.1 f)
// ============================================

/** Calendar months the bill covers: its billing period, or its month when that is all there is. */
export const billMonths = (bill: Pick<ComparatorBill, 'periodStart' | 'periodEnd' | 'month'>): number[] => {
	if (bill.periodStart && bill.periodEnd) {
		const months: number[] = [];
		const cursor = new Date(`${bill.periodStart.slice(0, 7)}-01T00:00:00Z`);
		const last = bill.periodEnd.slice(0, 7);
		for (let guard = 0; guard < 24; guard++) {
			months.push(cursor.getUTCMonth() + 1);
			if (cursor.toISOString().slice(0, 7) >= last) break;
			cursor.setUTCMonth(cursor.getUTCMonth() + 1);
		}
		return [...new Set(months)];
	}
	return bill.month ? [Number(bill.month.slice(5, 7))] : [];
};

/**
 * Periods with consumption that the calendar does not use in the billed
 * months: a sign the reading mixed them up. Empty for 2.0TD or when the bill
 * has no dates. A warning for the reviewer, never a block.
 */
export const checkPeriodCalendar = (bill: ComparatorBill): BillPeriod[] => {
	if (bill.tariff === 'T_20TD') return [];
	const months = billMonths(bill);
	if (months.length === 0) return [];
	const allowed = new Set<BillPeriod>(['P6', ...months.flatMap((month) => MONTH_PERIODS[month])]);
	return BILL_PERIODS.filter((period) => (bill.consumptionKwh[period] ?? 0) > 0 && !allowed.has(period));
};
